import {
  Button,
  DatePicker,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  datePickerValueToDateString,
  Select,
  stringToDatePickerValue,
} from '@civilgrid/ui/components';
import { useId, useState } from 'react';
import type { SubscriptionAccountType, SubscriptionStatusRow } from './types';

const ACCOUNT_TYPE_OPTIONS: { value: SubscriptionAccountType; label: string }[] = [
  { value: 'customer', label: 'Customer' },
  { value: 'demo', label: 'Demo (exempt)' },
  { value: 'test', label: 'Test (exempt)' },
  { value: 'internal', label: 'Internal (exempt)' },
];

export type SubscriptionEdit = { contractEndsOn: string | null; accountType: SubscriptionAccountType };

type EditSubscriptionDialogProps = {
  row: SubscriptionStatusRow | null;
  isSaving: boolean;
  onSave: (row: SubscriptionStatusRow, edit: SubscriptionEdit) => void;
  onClose: () => void;
};

export function EditSubscriptionDialog({ row, isSaving, onSave, onClose }: EditSubscriptionDialogProps) {
  return (
    <Dialog open={row !== null} onOpenChange={(open) => !open && onClose()}>
      {row && (
        <EditSubscriptionForm
          key={row.organizationId}
          row={row}
          isSaving={isSaving}
          onSave={onSave}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
}

function EditSubscriptionForm({
  row,
  isSaving,
  onSave,
  onClose,
}: Omit<EditSubscriptionDialogProps, 'row'> & { row: SubscriptionStatusRow }) {
  const salesforceNoteId = useId();
  const [contractEndsOn, setContractEndsOn] = useState(row.contractEndsOn ?? '');
  const [accountType, setAccountType] = useState<SubscriptionAccountType>(row.accountType);
  const salesforceOwnsDate = !!row.sfContractEndsOn;

  return (
    <DialogContent
      title={`Edit ${row.organizationName}`}
      description="Changes apply immediately and are recorded in the audit log."
      data-testid="subscriptions-edit-dialog"
      closeButtonProps={{ 'data-testid': 'subscriptions-edit-dialog-close' }}
    >
      <DialogBody className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <DatePicker
            label="Contract end date"
            value={stringToDatePickerValue(contractEndsOn)}
            onChange={(value) => setContractEndsOn(datePickerValueToDateString(value))}
            aria-describedby={salesforceOwnsDate ? salesforceNoteId : undefined}
            className="w-full"
            data-testid="subscriptions-edit-end-date"
          />
          {salesforceOwnsDate && (
            <p id={salesforceNoteId} className="text-xs text-grey-70">
              Salesforce has an end date for this deal, so the next sync replaces a manual date. Change it in Salesforce
              to keep it.
            </p>
          )}
        </div>
        <Select
          label="Account type"
          options={ACCOUNT_TYPE_OPTIONS}
          value={accountType}
          onValueChange={(value) => value && setAccountType(value as SubscriptionAccountType)}
          data-testid="subscriptions-edit-account-type"
        />
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={isSaving} data-testid="subscriptions-edit-cancel">
          Cancel
        </Button>
        <Button
          onClick={() => onSave(row, { contractEndsOn: contractEndsOn || null, accountType })}
          disabled={isSaving}
          data-testid="subscriptions-edit-save"
        >
          Save
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
